import { useNavigate } from 'react-router-dom';
import {
  BadgeCheck,
  ChevronRight,
  Clock,
  GraduationCap,
  Layers,
  Lock,
  PlayCircle,
  RotateCcw,
} from 'lucide-react';
import {
  type CursoAcademy,
} from './academyApi';
import {
  duracionLabel,
  duracionTotalLabel,
  duracionTotalSeg,
  tituloNumerado,
} from './academyFormato';
import { EsqueletoAcademia } from './EsqueletoAcademia';
import { EXAMEN_HABILITADO } from './examenHabilitado';
import { derivarProgreso, type ProgresoCurso } from './progresoDerivado';
import { useAcademia } from './useAcademia';
import { useEsperaExamen } from './useEsperaExamen';

/**
 * **Academia Atlas** — el centro de cursos de la plataforma
 * (ADR-2026-08-24-atlas-academy-seccion-nueva). Se llega desde la card de la home.
 *
 * Los cursos y el progreso salen de Supabase
 * (ADR-2026-08-25-la-academia-vive-en-supabase); antes venían en el bundle y en
 * `localStorage`. Por eso ahora hay un estado de carga que antes no existía.
 *
 * **El hero va sobre el lienzo pelado, sin panel propio.** No es sólo estética:
 * el shell le pone `background: transparent !important` al contenedor directo de
 * cualquier `<h1>` de sección, así que un hero con fondo se queda sin fondo justo
 * dentro del shell.
 */
export const AtlasAcademy = () => {
  const { cursos, progreso, cargando, error } = useAcademia();
  if (cargando) return <EsqueletoAcademia />;

  const lecciones = cursos.reduce((n, c) => n + c.lecciones.length, 0);

  return (
    <div className="mx-auto max-w-content px-4 pb-24 pt-2 sm:px-8 md:pb-8">
      {/* El `<h1>` cuelga directo de este div: es lo que hace que el shell le dé
          los 40px de aire de arriba que abren todas las secciones. */}
      <div>
        {/* Medallón en los colores de marca y no en oscuro
            (ADR-2026-08-25-el-medallon-va-en-azul-de-marca): era lo único oscuro
            de una pantalla clara y se leía como un hueco. */}
        <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#EBEFFE] text-brand-primary">
          <GraduationCap className="h-[22px] w-[22px]" strokeWidth={2} />
        </span>

        <h1 className="text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-brand-black/90 sm:text-[32px]">
          Academia Atlas
        </h1>

        <p className="mt-3 max-w-[62ch] text-[15px] leading-relaxed text-black/55 sm:text-[16px]">
          Cursos en video para aprender a usar Atlas de punta a punta. Cada curso son
          lecciones cortas que podés mirar a tu ritmo
          {EXAMEN_HABILITADO
            ? ', y al terminarlas rendís un examen de opción múltiple para aprobarlo.'
            : ', y tu progreso se guarda a medida que avanzás.'}
        </p>
      </div>

      {error && (
        <p className="mt-8 text-[14.5px] leading-relaxed text-red-700">{error}</p>
      )}

      {/* Sin video de introducción por ahora
          (ADR-2026-10-07-academia-curso-0-con-videos-reales): el que había era un
          provisorio, y la bienvenida ya es la primera lección del curso 0. */}

      <section className="mt-12">
        <h2 className="text-[22px] font-bold tracking-[-0.01em] text-brand-black/90">
          Cursos disponibles
        </h2>

        {/* Los tres datos que contestan "¿cuánto es esto?". Van acá y no en el
            hero (ADR-2026-08-25-los-datos-del-catalogo-van-con-los-cursos):
            describen lo que hay en la grilla de abajo. */}
        <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13.5px] font-medium text-black/50">
          <span className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-brand-primary" strokeWidth={2} />
            {cursos.length} {cursos.length === 1 ? 'curso' : 'cursos'}
          </span>
          <span className="flex items-center gap-2">
            <PlayCircle className="h-4 w-4 text-brand-primary" strokeWidth={2} />
            {lecciones} lecciones en video
          </span>
          {EXAMEN_HABILITADO ? (
            <span className="flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 text-brand-primary" strokeWidth={2} />
              Examen y aprobación por curso
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-brand-primary" strokeWidth={2} />
              {duracionLabel(cursos.reduce((n, c) => n + duracionTotalSeg(c), 0))} de video
            </span>
          )}
        </div>

        {/* Dos columnas desde `sm` y tres desde `lg`: con tres cursos, una grilla
            de tres en desktop cierra la fila exacta y no deja un hueco. */}
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cursos.map((curso) => (
            <CardCurso
              key={curso.id}
              curso={curso}
              cursos={cursos}
              progreso={derivarProgreso(curso, progreso.get(curso.id))}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

/**
 * Qué dice el pie de una card y a dónde lleva
 * (ADR-2026-08-24-el-pie-de-la-card-dice-el-estado).
 *
 * Función pura con los casos EN ORDEN de precedencia, en vez de una pila de
 * ternarios en el JSX: son seis estados y cada uno cambia tres cosas a la vez.
 * "Aprobado" gana sobre todo, y "en espera" gana sobre "podés rendir".
 *
 * **El destino es SIEMPRE el curso** (ADR-2026-08-24-la-card-siempre-entra-al-curso):
 * una card de catálogo es la puerta al contenido, y saltear los videos para caer
 * en el examen le saca al usuario la decisión de qué quiere hacer.
 */
function estadoDelPie(
  curso: CursoAcademy,
  progreso: ProgresoCurso,
  espera: { listo: boolean; etiqueta: string },
): { info: string; Icono: typeof BadgeCheck; tono: string; cta: string } {
  if (!EXAMEN_HABILITADO) return estadoDelPieSinExamen(progreso);

  if (progreso.aprobado) {
    return {
      info: `Aprobado con ${progreso.mejorPuntaje ?? curso.puntajeMinimo}%`,
      Icono: BadgeCheck,
      tono: 'text-emerald-700',
      cta: 'Repasar curso',
    };
  }

  if (progreso.ultimoIntento !== null && !espera.listo) {
    return {
      info: `Podés reintentar en ${espera.etiqueta}`,
      Icono: Lock,
      tono: 'text-amber-700',
      cta: 'Ir al curso',
    };
  }

  if (progreso.ultimoIntento !== null) {
    return {
      info: `Todavía no aprobaste`,
      Icono: RotateCcw,
      tono: 'text-amber-700',
      cta: 'Ir al curso',
    };
  }

  if (progreso.completo) {
    return {
      info: 'Sólo te falta el examen',
      Icono: BadgeCheck,
      tono: 'text-brand-primary',
      cta: 'Ir al curso',
    };
  }

  if (progreso.vistas > 0) {
    const faltan = progreso.total - progreso.vistas;
    return {
      info: `${faltan === 1 ? 'Te falta' : 'Te faltan'} ${faltan} ${faltan === 1 ? 'lección' : 'lecciones'}`,
      Icono: PlayCircle,
      tono: 'text-black/45',
      cta: 'Continuar',
    };
  }

  return {
    info: `Examen de ${curso.cantidadPreguntas} preguntas`,
    Icono: BadgeCheck,
    tono: 'text-black/40',
    cta: 'Comenzar curso',
  };
}

/**
 * El pie sin examen (ADR-2026-10-07-la-academia-sin-examen-por-ahora): el estado es
 * sólo el avance por las lecciones. Terminar el curso es el final del recorrido, y
 * "aprobado" no se muestra aunque la fila lo tenga de antes: hoy no hay forma de
 * aprobar y un sello que nadie más puede conseguir confunde.
 */
function estadoDelPieSinExamen(
  progreso: ProgresoCurso,
): { info: string; Icono: typeof BadgeCheck; tono: string; cta: string } {
  if (progreso.completo) {
    return {
      info: 'Curso completado',
      Icono: BadgeCheck,
      tono: 'text-emerald-700',
      cta: 'Repasar curso',
    };
  }

  if (progreso.vistas > 0) {
    const faltan = progreso.total - progreso.vistas;
    return {
      info: `${faltan === 1 ? 'Te falta' : 'Te faltan'} ${faltan} ${faltan === 1 ? 'lección' : 'lecciones'}`,
      Icono: PlayCircle,
      tono: 'text-black/45',
      cta: 'Continuar',
    };
  }

  return {
    info: 'A tu ritmo',
    Icono: PlayCircle,
    tono: 'text-black/40',
    cta: 'Comenzar curso',
  };
}

function CardCurso({
  curso,
  cursos,
  progreso,
}: {
  curso: CursoAcademy;
  cursos: CursoAcademy[];
  progreso: ProgresoCurso;
}) {
  const navigate = useNavigate();
  // Sin examen no hay espera que contar: con 0 el hook no arranca su intervalo.
  const espera = useEsperaExamen(
    progreso.ultimoIntento,
    EXAMEN_HABILITADO ? curso.esperaMinutos : 0,
  );
  const estado = estadoDelPie(curso, progreso, espera);

  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-black/[0.06] bg-white transition-all sm:hover:-translate-y-0.5 sm:hover:border-black/[0.1] sm:hover:shadow-[0_8px_28px_rgba(16,24,45,0.07)]">
      <button
        type="button"
        onClick={() => navigate(`/academia/${curso.slug}`)}
        className="flex flex-1 flex-col text-left"
      >
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#F1F2F6]">
          <img
            src={curso.portada}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 sm:group-hover:scale-[1.04]"
          />
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />

          <span
            className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11.5px] font-semibold ${curso.tono}`}
          >
            {curso.nivel}
          </span>

          <span className="absolute bottom-3 left-3 flex items-center gap-3 text-[12px] font-medium text-white">
            <span className="flex items-center gap-1.5">
              <PlayCircle className="h-3.5 w-3.5" strokeWidth={2} />
              {curso.lecciones.length} lecciones
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" strokeWidth={2} />
              {duracionTotalLabel(curso)}
            </span>
          </span>
        </div>

        <div className="flex flex-1 flex-col p-4">
          <h3 className="text-[16.5px] font-semibold leading-snug tracking-[-0.01em] text-brand-black/90">
            {tituloNumerado(curso, cursos)}
          </h3>

          {/* La bajada ocupa SIEMPRE dos líneas
              (ADR-2026-08-24-la-card-reserva-el-alto-de-la-bajada): con una de una
              línea y otra de dos, las barras de las tres cards quedaban a distinta
              altura. `leading-[1.6]` explícito porque el `min-h` tiene que medir
              exactamente dos renglones. */}
          <p className="mt-1.5 line-clamp-2 min-h-[44px] text-[13.5px] leading-[1.6] text-black/50">
            {curso.bajada}
          </p>

          {/* `mt-auto` acá y no en el pie: empuja al fondo el GRUPO progreso + pie,
              así la barra queda anclada abajo y alineada entre cards. */}
          <div className="mt-auto pt-4">
            <div className="flex items-center justify-between text-[12px] font-medium">
              <span className="text-black/45">
                {progreso.vistas} de {progreso.total} lecciones
              </span>
              <span
                className={`tabular-nums font-semibold ${
                  progreso.completo ? 'text-emerald-600' : 'text-brand-primary'
                }`}
              >
                {progreso.porcentaje}%
              </span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-black/[0.07]">
              <div
                className={`h-full rounded-full transition-[width] duration-500 ${
                  progreso.completo ? 'bg-emerald-500' : 'bg-brand-primary'
                }`}
                style={{ width: `${progreso.porcentaje}%` }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 pt-4">
            {/* `truncate`: el contador es el texto más largo de los seis estados y
                no puede empujar al CTA fuera de la card. */}
            <span
              className={`flex min-w-0 items-center gap-1.5 text-[12.5px] font-medium ${estado.tono}`}
            >
              <estado.Icono className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
              <span className="truncate tabular-nums">{estado.info}</span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-[#EBEFFE] px-2.5 py-1.5 text-[12.5px] font-semibold text-brand-primary transition-colors group-hover:bg-[#DFE6FD]">
              {estado.cta}
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </button>
    </article>
  );
}
